import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Icon, Label, List, Loader, Message, Segment
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { TeamDto } from '../../app/models/Team';
import { CreateChallengeModal } from './CreateChallengeModal';

interface Props {
  team: TeamDto;
}

const STATUS_COLOR: Record<string, 'green' | 'yellow' | 'red' | 'grey' | 'teal' | 'blue' | 'orange'> = {
  Pending:     'yellow',
  Accepted:    'green',
  Rejected:    'red',
  Open:        'blue',
  TeamApplied: 'orange',
  Scheduled:   'teal',
  Completed:   'green',
  Cancelled:   'grey',
};

export default observer(function TeamMatchesTab({ team }: Props) {
  const { teamMatchStore } = useStore();
  const { t } = useTranslation();

  useEffect(() => {
    teamMatchStore.loadTeamMatches(team.id);
  }, [team.id]);

  const active = teamMatchStore.teamMatches.filter(
    m => !['Completed', 'Rejected', 'Cancelled'].includes(m.status)
  );
  const history = teamMatchStore.teamMatches.filter(
    m => ['Completed', 'Rejected', 'Cancelled'].includes(m.status)
  );

  return (
    <Segment style={{ padding: 0, border: 'none', boxShadow: 'none' }}>

      {/* Create challenge button — captain only */}
      {team.isCurrentUserCaptain && team.status === 'Active' && (
        <div style={{ marginBottom: 16 }}>
          <CreateChallengeModal teamId={team.id} sportId={team.sportID} />
        </div>
      )}

      {teamMatchStore.loading && (
        <Loader active inline="centered" style={{ margin: '30px auto' }} />
      )}

      {!teamMatchStore.loading && teamMatchStore.teamMatches.length === 0 && (
        <Message info>
          <Icon name="shield alternate" />
          {t('teamMatch.noMatches')}
          {team.isCurrentUserCaptain && (
            <span style={{ marginLeft: 8 }}>
              {t('teamMatch.noMatchesHint')}
            </span>
          )}
        </Message>
      )}

      {/* Active duels */}
      {active.length > 0 && (
        <>
          <div style={{ fontWeight: 700, color: '#666', fontSize: '0.85em', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('teamMatch.activeTitle')}
          </div>
          <List divided relaxed style={{ marginBottom: 20 }}>
            {active.map(m => (
              <List.Item
                key={m.id}
                as={Link}
                to={`/teammatches/${m.id}`}
                style={{ padding: '10px 0', cursor: 'pointer' }}
              >
                <List.Content>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>
                        {m.teamAName}
                        <span style={{ color: '#999', margin: '0 8px' }}>vs</span>
                        {m.teamBName || <span style={{ color: '#bbb', fontStyle: 'italic' }}>?</span>}
                      </span>
                      <div style={{ fontSize: '0.8em', color: '#999', marginTop: 2 }}>
                        <Icon name={m.type === 'Direct' ? 'lock' : 'unlock'} size="small" />
                        {t(`teamMatch.type.${m.type.toLowerCase()}`)}
                        {m.scheduledAt && (
                          <span style={{ marginLeft: 8 }}>
                            <Icon name="calendar" size="small" />
                            {format(new Date(m.scheduledAt), 'dd MMM yyyy, HH:mm')}
                          </span>
                        )}
                      </div>
                    </div>
                    <Label
                      size="small"
                      color={STATUS_COLOR[m.status] || 'grey'}
                    >
                      {t(`teamMatch.status.${m.status.toLowerCase()}`)}
                    </Label>
                  </div>
                </List.Content>
              </List.Item>
            ))}
          </List>
        </>
      )}

      {/* Match history */}
      {history.length > 0 && (
        <>
          <div style={{ fontWeight: 700, color: '#666', fontSize: '0.85em', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('teamMatch.historyTitle')}
          </div>
          <List divided relaxed>
            {history.map(m => (
              <List.Item
                key={m.id}
                as={Link}
                to={`/teammatches/${m.id}`}
                style={{ padding: '10px 0', cursor: 'pointer' }}
              >
                <List.Content>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>
                        {m.teamAName}
                        {m.result && (
                          <span style={{ margin: '0 8px', fontWeight: 900, fontSize: '1.05em', color: '#333' }}>
                            {m.result.scoreA}:{m.result.scoreB}
                          </span>
                        )}
                        {!m.result && <span style={{ color: '#999', margin: '0 8px' }}>vs</span>}
                        {m.teamBName}
                      </span>
                      <div style={{ fontSize: '0.8em', color: '#999', marginTop: 2 }}>
                        {m.scheduledAt && format(new Date(m.scheduledAt), 'dd MMM yyyy')}
                      </div>
                    </div>
                    <Label
                      size="small"
                      color={STATUS_COLOR[m.status] || 'grey'}
                    >
                      {t(`teamMatch.status.${m.status.toLowerCase()}`)}
                    </Label>
                  </div>
                </List.Content>
              </List.Item>
            ))}
          </List>
        </>
      )}
    </Segment>
  );
});